import { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { venueUrl } from "../utils/venueUrl";
import { API } from "../App";
import { useTranslation } from "react-i18next";
import Header from "../components/layout/Header";
import Footer from "../components/layout/Footer";
import { MapPin, Star, ArrowRight, Layers } from "lucide-react";
import axios from "axios";

export function CollectionsListPage() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const L = (obj, f) => (lang === "el" ? (obj?.[`${f}_el`] || obj?.[f] || "") : (obj?.[f] || ""));
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await axios.get(`${API}/collections`);
        setCollections(res.data);
      } catch (error) { console.error(error); }
      finally { setLoading(false); }
    };
    fetch();
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="pt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <h1 className="text-4xl font-medium text-foreground mb-2" data-testid="collections-heading">
            {t("collections_page:title", { defaultValue: "Curated Collections" })}
          </h1>
          <p className="text-lg text-muted-foreground mb-10">
            {t("collections_page:subtitle", { defaultValue: "Hand-picked venue selections for every occasion." })}
          </p>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3].map((i) => <div key={i} className="animate-pulse bg-muted h-64 rounded-xl" />)}
            </div>
          ) : collections.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {collections.map((col) => (
                <Link key={col.collection_id} to={`/collections/${col.slug}`} className="group" data-testid={`collection-link-${col.slug}`}>
                  <div className="bg-card rounded-xl border border-border overflow-hidden hover:shadow-lg transition-all">
                    <div className="h-48 bg-gradient-to-br from-primary/10 to-primary/5 relative overflow-hidden">
                      {col.hero_image ? (
                        <img src={col.hero_image} alt={L(col, "name")} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                      ) : (
                        <div className="flex items-center justify-center h-full"><Layers className="h-12 w-12 text-primary/20" /></div>
                      )}
                    </div>
                    <div className="p-5">
                      <h3 className="text-lg font-medium text-foreground group-hover:text-primary transition-colors">{L(col, "name")}</h3>
                      <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{L(col, "description")}</p>
                      <div className="flex items-center justify-between mt-4">
                        <span className="text-xs text-muted-foreground">
                          {t("collections_page:venue_count", { count: (col.venues || []).length })}
                        </span>
                        <ArrowRight className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors" />
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <Layers className="h-16 w-16 text-muted-foreground/30 mx-auto mb-4" />
              <h2 className="text-xl font-medium text-foreground mb-2">
                {t("collections_page:empty_title", { defaultValue: "No collections yet" })}
              </h2>
              <p className="text-muted-foreground">
                {t("collections_page:empty_sub", { defaultValue: "Check back soon for curated venue selections!" })}
              </p>
            </div>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
}

export function CollectionDetailPage() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const L = (obj, f) => (lang === "el" ? (obj?.[`${f}_el`] || obj?.[f] || "") : (obj?.[f] || ""));
  const { slug } = useParams();
  const [collection, setCollection] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await axios.get(`${API}/collections/${slug}`);
        setCollection(res.data);
      } catch (error) { console.error(error); }
      finally { setLoading(false); }
    };
    fetch();
  }, [slug]);

  if (loading) return <div className="min-h-screen bg-background"><Header /><div className="pt-20 max-w-7xl mx-auto px-4 py-12"><div className="animate-pulse bg-muted h-64 rounded-xl" /></div></div>;
  if (!collection) return <div className="min-h-screen bg-background"><Header /><div className="pt-20 text-center py-24"><h1 className="text-2xl font-medium">{t("collections_page:not_found", { defaultValue: "Collection not found" })}</h1></div></div>;

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="pt-20">
        {/* Hero */}
        <div className="relative h-64 bg-gradient-to-br from-primary/10 to-primary/5 overflow-hidden">
          {collection.hero_image && (
            <img src={collection.hero_image} alt={L(collection, "name")} className="w-full h-full object-cover" />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-background/80 to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 p-8 max-w-7xl mx-auto">
            <h1 className="text-3xl font-medium text-foreground" data-testid="collection-detail-title">{L(collection, "name")}</h1>
            <p className="text-muted-foreground mt-1">{L(collection, "description")}</p>
          </div>
        </div>

        {/* Venues */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <p className="text-sm text-muted-foreground mb-6">
            {t("collections_page:venues_in_collection", { count: (collection.venues || []).length })}
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {(collection.venues || []).map((venue) => (
              <Link key={venue.venue_id} to={venueUrl(venue)} className="group" data-testid={`collection-venue-${venue.venue_id}`}>
                <div className="bg-card rounded-xl border border-border overflow-hidden hover:shadow-lg transition-shadow">
                  <div className="h-48 bg-muted relative overflow-hidden">
                    {venue.hero_image ? (
                      <img src={venue.hero_image} alt={L(venue, "name")} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                    ) : (
                      <div className="flex items-center justify-center h-full"><MapPin className="h-8 w-8 text-muted-foreground/30" /></div>
                    )}
                  </div>
                  <div className="p-4">
                    <h3 className="font-medium text-foreground group-hover:text-primary transition-colors">{L(venue, "name")}</h3>
                    <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{L(venue, "short_description")}</p>
                    <div className="flex items-center justify-between mt-3">
                      <span className="text-xs text-muted-foreground capitalize">{venue.city_id}</span>
                      {venue.average_rating > 0 && (
                        <span className="text-xs flex items-center gap-1">
                          <Star className="h-3 w-3 text-amber-500 fill-amber-500" /> {venue.average_rating.toFixed(1)}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
